import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../auth.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { MaintenanceBannerComponent } from '../../component/maintenance-banner/maintenance-banner.component';
import { LanguageSelectComponent } from '../../component/language-select/language-select.component';
import { LanguageService } from '../../core/services/language.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MaintenanceBannerComponent, LanguageSelectComponent, TranslatePipe],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private maintenanceService = inject(MaintenanceService);
  readonly languageService = inject(LanguageService);
  private destroy$ = new Subject<void>();

  loading = false;
  apiError: string | null = null;
  sessionExpired = false;
  maintenanceBanner: string | null = null;
  showForgotPasswordModal = false;
  forgotPasswordEmail = '';
  forgotPasswordLoading = false;
  forgotPasswordSuccess = false;
  showAdminModal = false;
  adminPassword = '';
  adminError = false;
  showPassword = false;

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor() {
    this.sessionExpired = this.route.snapshot.queryParams['expired'] === '1';
  }

  ngOnInit(): void {
    this.maintenanceService.getBanner().pipe(takeUntil(this.destroy$)).subscribe(msg => {
      this.maintenanceBanner = msg;
    });
  }

  submit(): void {
    if (this.loading) return;

    // si invalide, on force l’affichage des erreurs
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.apiError = null;
    this.loading = true;

    const { email, password } = this.form.value as { email: string; password: string };

    // login(email, password) - ensure the service signature matches this
    this.auth.login(email, password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: () => {
        this.loading = false;
        this.router.navigateByUrl('/');
      },
      error: (err) => {
        console.error('[LoginComponent] login ERROR', this.sanitizeForLog(err));
        this.loading = false;
        this.apiError = err?.error?.message || 'Identifiants incorrects.';
      },
    });
  }

  toRegister(): void {
    this.showAdminModal = true;
    this.adminPassword = '';
    this.adminError = false;
  }

  closeAdminModal(): void {
    this.showAdminModal = false;
    this.adminPassword = '';
    this.adminError = false;
  }

  validateAdminPassword(): void {
    // TODO: Replace hardcoded password with secure authentication
    const ADMIN_PASSWORD = 'admin123456';
    if (this.adminPassword === ADMIN_PASSWORD) {
      this.closeAdminModal();
      this.router.navigateByUrl('/register');
    } else {
      this.adminError = true;
    }
  }

  openForgotPasswordModal(): void {
    this.showForgotPasswordModal = true;
    this.forgotPasswordEmail = '';
    this.forgotPasswordSuccess = false;
  }

  closeForgotPasswordModal(): void {
    this.showForgotPasswordModal = false;
    this.forgotPasswordLoading = false;
    this.forgotPasswordSuccess = false;
  }

  sendResetEmail(): void {
    if (!this.forgotPasswordEmail || this.forgotPasswordLoading) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.forgotPasswordEmail)) {
      this.apiError = 'Format d\'email invalide';
      return;
    }

    this.forgotPasswordLoading = true;
    this.apiError = null;

    this.auth.forgotPassword(this.forgotPasswordEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: () => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordSuccess = true;
      },
      error: (err) => {
        this.forgotPasswordLoading = false;
        console.error('Forgot password error:', this.sanitizeForLog(err));
        // En cas d'erreur, on affiche quand même le succès pour des raisons de sécurité
        this.forgotPasswordSuccess = true;
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  selectLang(code: string): void {
    this.languageService.setLang(code);
  }

  private sanitizeForLog(input: any): string {
    if (input == null) return 'null';
    const str = String(input);
    return str.replace(/[\r\n\t]/g, ' ').substring(0, 200);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
